import React, { useEffect } from 'react'
import { generatePath, useLocation, useParams } from 'react-router-dom'

import type { GetRemoteStringFunction } from '@acorns/web-utils'
import { useAnalyticsClient } from '@acorns/web-utils'
import { useRemoteStrings } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'

import { trackPortfolioScreenViewedPayload } from 'generated/trackingEvents'
import {
  InvestmentAccount,
  InvestmentProduct,
  Maybe,
  Portfolio,
  PortfolioTheme,
  SelfDirectedPortfolio,
  User,
} from 'generated/types'
import { useGetEarlyAccounts } from 'pages/early/hooks/useGetEarlyAccounts'
import {
  FormattedDisplayedTheme,
  PortfolioInvestmentProduct,
} from 'pages/portfolio/types'
import { routes } from 'router/routes'
import { reopenFlowTypeAnalyticsProps } from 'utils/analytics/reopenFlowAnalytics'

import {
  getCurrentProductPortfolio,
  investmentProductMap,
  orderPortfolioSecurityAllocations,
  orderSecurities,
} from '../helperFunctions'
import ProductPortfoliosPassions from '../queries/allProductsPortfoliosPassionsQuery.gql'
import RecurringInvestmentSettings from '../queries/recurringInvestmentSettings.gql'
import TierSubscriptionForUser from '../queries/tierSubscription.gql'

const resolveDisplayedTheme = (portfolioTheme: string) => {
  switch (portfolioTheme) {
    case PortfolioTheme.Standard:
      return FormattedDisplayedTheme.STANDARD
    case PortfolioTheme.Sustainable:
      return FormattedDisplayedTheme.SUSTAINABLE
    default:
      return portfolioTheme
  }
}

interface AllProductPortfoliosResponse {
  user: User
}

const getPortfolioDescriptionKey = (
  portfolioTheme: PortfolioTheme | string,
  portfolioRisk: string,
  portfolioInvestmentProduct: string,
  getRemoteString: GetRemoteStringFunction,
  StyledRemoteString: React.FC<{ stringKey: string; screen?: string }>,
) => {
  const specifier =
    portfolioInvestmentProduct === PortfolioInvestmentProduct.Invest
      ? `${portfolioRisk?.toLowerCase().replace(/ /g, '_')}.`
      : ''

  if (portfolioInvestmentProduct === PortfolioInvestmentProduct.Early) {
    return portfolioTheme === PortfolioTheme.Sustainable
      ? getRemoteString({ key: `portfolio.early.sustainable` })
      : getRemoteString({ key: `portfolio.early.core` })
  }

  if (portfolioTheme === PortfolioTheme.Sustainable) {
    return (
      <StyledRemoteString
        stringKey={`portfolio.${portfolioInvestmentProduct}.${specifier}sustainable.v1.markdown`}
        screen="portfolio"
      />
    )
  } else {
    return (
      <StyledRemoteString
        stringKey={`portfolio.${portfolioInvestmentProduct}.${specifier}core.v1.markdown`}
        screen="portfolio"
      />
    )
  }
}

type RouteParameters = { beneficiaryId: string }

const getMultiPortfolioDetails = (
  investmentAccount: Maybe<InvestmentAccount> | undefined,
) => {
  // Need to do these typecasts because __typename doesn't exist on the BasePortfolio type
  const basePortfolio = investmentAccount?.portfolios?.find(
    (portfolio) => (portfolio as Portfolio)?.__typename === 'Portfolio',
  ) as Portfolio

  // Need to do these typecasts because __typename doesn't exist on the BasePortfolio type
  const customPortfolio = investmentAccount?.portfolios?.find(
    (portfolio) =>
      (portfolio as SelfDirectedPortfolio)?.__typename ===
      'SelfDirectedPortfolio',
  ) as SelfDirectedPortfolio

  // Need to do these typecasts because __typename doesn't exist on the BasePortfolio type
  const recommendedPortfolio =
    investmentAccount?.recommendedPortfolio as Portfolio

  const minimumManagedAllocationPercent =
    recommendedPortfolio?.minimumManagedAllocationPercent ?? 0
  const maximumCustomAllocationPercent = 100 - minimumManagedAllocationPercent
  const theme = investmentAccount?.portfolio?.theme

  return {
    basePortfolio,
    customPortfolio,
    minimumManagedAllocationPercent,
    maximumCustomAllocationPercent,
    theme,
  }
}

const useCurrentPortfolioDetails = (
  investmentAccounts: Maybe<InvestmentAccount[]> | undefined,
  portfolioInvestmentProduct: PortfolioInvestmentProduct,
  routeParameters: RouteParameters,
) => {
  const investmentProduct = investmentProductMap[portfolioInvestmentProduct]
  const { earlyAccount, isLoadingBeneficiaryAccounts } =
    useGetEarlyAccounts(routeParameters)
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()

  let investmentAccount = null
  let beneficiaryName = null

  if (investmentProduct === InvestmentProduct.Early) {
    investmentAccount = earlyAccount?.investmentAccount
    beneficiaryName = earlyAccount?.firstName
  } else {
    investmentAccount = investmentAccounts?.filter(
      (account: InvestmentAccount) => account.product === investmentProduct,
    )[0]
  }

  const multiPortfolioDetails = getMultiPortfolioDetails(investmentAccount)
  const managedPortfolio =
    multiPortfolioDetails?.basePortfolio ?? investmentAccount?.portfolio
  const currentPortfolioRisk = managedPortfolio?.name ?? ''
  const portfolioTheme = multiPortfolioDetails?.theme ?? ''
  const selfDirectedPortfolioStatus =
    multiPortfolioDetails?.customPortfolio?.status

  const yearToDateEarnings =
    investmentAccount?.yearToDateReturn?.marketValue?.value ?? 0
  const yearToDateReturn = investmentAccount?.yearToDateReturn?.percent ?? 0
  const currentAccountBalance = investmentAccount?.currentBalance?.value
  const currentRiskPortfolioData = investmentAccount?.portfolio as Portfolio
  if (currentRiskPortfolioData?.portfolioSecurityAllocations) {
    currentRiskPortfolioData.portfolioSecurityAllocations = orderSecurities(
      currentRiskPortfolioData.portfolioSecurityAllocations,
    )
  }
  const recommendedPortfolio = investmentAccount?.recommendedPortfolio?.name
  const investmentAccountId = investmentAccount?.id
  const totalBonds = investmentAccount?.totalBonds?.value
  const totalEquities = investmentAccount?.totalEquities?.value

  const currentPortfolioDescription = getPortfolioDescriptionKey(
    portfolioTheme,
    currentPortfolioRisk,
    portfolioInvestmentProduct,
    getRemoteString,
    StyledRemoteString,
  )

  const currentPortfolioTheme = resolveDisplayedTheme(portfolioTheme)

  return {
    currentPortfolioRisk,
    currentPortfolioTheme,
    currentAccountBalance,
    currentRiskPortfolioData,
    recommendedPortfolio,
    investmentAccountId,
    isLoadingBeneficiaryAccounts,
    currentPortfolioDescription,
    yearToDateEarnings,
    yearToDateReturn,
    beneficiaryName,
    totalBonds,
    totalEquities,
    accountStatus: investmentAccount?.status,
    selfDirectedPortfolioStatus,
    ...multiPortfolioDetails,
  }
}

const usePortfolioData = ({
  isLongReopen,
}: { isLongReopen?: boolean } = {}) => {
  const { state } = useLocation<{ referrer?: boolean }>()

  const location = useLocation()
  const routeParameters = useParams<RouteParameters>()
  const analytics = useAnalyticsClient()
  const currentProductPortfolio = getCurrentProductPortfolio(location)

  const {
    data: allProductsPortfolioData,
    loading: isLoadingProductsPortfolioData,
  } = useQuery<AllProductPortfoliosResponse, {}>(ProductPortfoliosPassions, {
    fetchPolicy: 'network-only',
  })

  const {
    data: tierSubscriptionForUserData,
    loading: isLoadingTierSubscriptionForUser,
  } = useQuery(TierSubscriptionForUser, {
    fetchPolicy: 'network-only',
  })

  const subscriptionTier =
    tierSubscriptionForUserData?.tierSubscriptionByUserId?.tier?.name
  const subscriptionKey =
    tierSubscriptionForUserData?.tierSubscriptionByUserId?.tier?.key

  // decide what portfolio data to display
  const isProductInvest =
    currentProductPortfolio === PortfolioInvestmentProduct.Invest
  const isProductLater =
    currentProductPortfolio === PortfolioInvestmentProduct.Later
  const isProductEarly =
    currentProductPortfolio === PortfolioInvestmentProduct.Early

  const investmentAccounts =
    allProductsPortfolioData?.user?.investmentAccounts?.filter(
      (account) => !account.closedAt,
    )

  const portfolioDetails = useCurrentPortfolioDetails(
    investmentAccounts,
    currentProductPortfolio,
    routeParameters,
  )

  let productType = InvestmentProduct.Invest
  if (isProductLater) {
    productType = InvestmentProduct.Later
  } else if (isProductEarly) {
    productType = InvestmentProduct.Early
  }

  const {
    data: recurringSettingsForUserData,
    loading: isLoadingRecurringSettingsForUser,
  } = useQuery(RecurringInvestmentSettings, {
    fetchPolicy: 'network-only',
    variables: {
      product: productType,
    },
  })

  const isLoadingData =
    isLoadingProductsPortfolioData ||
    isLoadingTierSubscriptionForUser ||
    isLoadingRecurringSettingsForUser ||
    portfolioDetails.isLoadingBeneficiaryAccounts

  const recurringSettings =
    recurringSettingsForUserData?.investmentAccountsByUserId[0]
      ?.recurringInvestmentSettings
  const hasRecurring = recurringSettings ? true : false

  useEffect(() => {
    if (!isLoadingData) {
      const portfolioItems = [
        { currentRisk: portfolioDetails?.currentPortfolioRisk },
        { currentTheme: portfolioDetails?.currentPortfolioTheme },
        { investmentAccountId: portfolioDetails?.investmentAccountId },
        { portfolioHome: location.pathname },
        { currentProduct: currentProductPortfolio },
        { beneficiaryId: routeParameters.beneficiaryId },
        { subscriptionKey },
        { subscriptionTier },
        { recommendedPortfolio: portfolioDetails?.recommendedPortfolio },
        { beneficiaryName: portfolioDetails?.beneficiaryName },
      ]

      // TODO: Determine whether we can instead use react global state for this.
      localStorage.setItem(
        'portfolio',
        JSON.stringify({
          portfolioItems,
        }),
      )

      analytics.track(
        ...trackPortfolioScreenViewedPayload({
          investmentProduct: currentProductPortfolio,
          portfolioType: portfolioDetails?.currentPortfolioTheme,
          portfolioRisk: portfolioDetails?.currentPortfolioRisk,
          tier: subscriptionTier,
          // we only want to include these properties if we're in the reopen flow
          ...(isLongReopen !== undefined
            ? {
                funnel: 'reopen',
                ...reopenFlowTypeAnalyticsProps(isLongReopen),
              }
            : {}),
        }),
      )
    }
  }, [isLoadingData])

  let backOverride = routes.invest.core
  if (isProductLater) {
    backOverride = routes.later.index
  } else if (state?.referrer) {
    backOverride = state.referrer
  } else if (routeParameters.beneficiaryId && isProductEarly) {
    backOverride = generatePath(routes.early.details, {
      beneficiaryId: routeParameters.beneficiaryId,
    })
  }

  // Ensuring order expected by UI
  if (portfolioDetails?.basePortfolio?.portfolioSecurityAllocations) {
    portfolioDetails.basePortfolio.portfolioSecurityAllocations =
      orderPortfolioSecurityAllocations(
        portfolioDetails.basePortfolio.portfolioSecurityAllocations,
      )
  }

  return {
    backOverride,
    currentProductPortfolio,
    portfolioDetails,
    isProductInvest,
    isProductLater,
    isProductEarly,
    routeParameters,
    subscriptionKey,
    subscriptionTier,
    isLoadingData,
    hasRecurring,
  }
}

export default usePortfolioData
